import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQuery, toQueryParams } from 'helpers/api';
import { stringDate } from 'helpers/date';

const REPORT_MARKER = '\n__CHECKMATE_REPORT__\n';

const findBytes = (haystack, needle) => {
  if (needle.length === 0 || haystack.length < needle.length) return -1;
  const last = haystack.length - needle.length;
  for (let i = 0; i <= last; i += 1) {
    let matched = true;
    for (let j = 0; j < needle.length; j += 1) {
      if (haystack[i + j] !== needle[j]) {
        matched = false;
        break;
      }
    }
    if (matched) return i;
  }
  return -1;
};

const stripReportPadding = async blob => {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  const marker = new TextEncoder().encode(REPORT_MARKER);
  const at = findBytes(bytes, marker);
  if (at === -1) return blob;
  return new Blob([bytes.subarray(at + marker.length)]);
};

export const reportsApi = createApi({
  reducerPath: 'reportsApi',
  baseQuery: createBaseQuery('reports', { timeout: 0 }),
  endpoints: builder => ({
    getReport: builder.query({
      queryFn: async ({ id, params }, api, extraOptions, baseQuery) => {
        const stripPadding = params?.deliver === 'email' || id === 'xero';
        const result = await baseQuery({
          url: `/${id}?${toQueryParams(params)}`,
          responseHandler: async response => {
            const blob = await response.blob();
            if (!response.ok) {
              const text = await blob.text();
              try {
                return JSON.parse(text);
              } catch {
                return { error: { message: text || 'The report could not be created.' } };
              }
            }
            if (!stripPadding) return blob;
            return stripReportPadding(blob);
          },
          cache: 'no-cache',
        });
        if (result.error) return { error: result.error };
        const type = result.meta?.response?.headers?.get('content-type') || '';
        if (type.includes('application/json')) {
          const json = JSON.parse(await result.data.text());
          if (!json.emailed) {
            return {
              error: {
                status: 500,
                data: { error: { message: json.error || 'The report could not be emailed.' } },
              },
            };
          }
          return { data: json };
        }
        if (stripPadding) {
          const text = await result.data.text();
          if (text.startsWith('error\n')) {
            const message = text.slice('error\n'.length).trim();
            return {
              error: {
                status: 500,
                data: { error: { message: message || 'The report could not be created.' } },
              },
            };
          }
        }
        saveAs(result.data, `${stringDate()}_${id}.csv`);
        return { data: null };
      },
    }),
  }),
});

export const { useLazyGetReportQuery } = reportsApi;
